import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { InputComponent } from "../../shared/input/input.component";
import { ButtonComponent } from "../../shared/button/button.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { TitleComponent } from "../../shared/title/title.component";
import { GigyaService } from "../../services/gigya.service";
import { OrganizationPickerService } from "../../services/organization-picker.service";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { BreadcrumbComponent } from "../../shared/breadcrumb/breadcrumb.component";
import { PasswordService, PasswordApiError } from "../../services/password.service";

@Component({
  selector: "app-password-reset-login",
  templateUrl: "./password-reset-login.component.html",
  styleUrls: ["./password-reset-login.component.css"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    RouterLink,
    LayoutComponent,
    TitleComponent,
    BreadcrumbComponent,
    SpinnerComponent,
  ],
})
export class PasswordResetLoginComponent implements OnInit {
  passwordForm: FormGroup;
  validations = { minLength: false, uppercase: false, number: false, specialChar: false };
  button_clicked = false;
  token: string | null = null;
  email: string | null = null;
  apiError: PasswordApiError | null = null;

  constructor(
    private fb: FormBuilder,
    private gigyaService: GigyaService,
    private organizationPicker: OrganizationPickerService,
    private router: Router,
    private route: ActivatedRoute,
    private passwordService: PasswordService,
  ) {
    this.passwordForm = this.fb.group({
      newPassword: ["", [Validators.required, this.passwordService.validator()]],
      confirmPassword: ["", [Validators.required]],
    });
    this.passwordForm.get("newPassword")?.valueChanges.subscribe(() => {
      if (this.apiError) {
        this.button_clicked = false;
        this.apiError = null;
      }
    });
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get("token");
    this.email = this.route.snapshot.queryParamMap.get("email");
  }

  get passwordMismatch(): boolean {
    const newPassword = this.passwordForm.get("newPassword")?.value;
    const confirmPassword = this.passwordForm.get("confirmPassword")?.value;
    return newPassword && confirmPassword && newPassword !== confirmPassword;
  }

  checkPassword() {
    const control = this.passwordForm.get("newPassword");
    this.validations = this.passwordService.checkStrength(control?.value || "");
    control?.updateValueAndValidity({ emitEvent: false });
  }

  async onSubmit() {
    if (!this.passwordForm.valid || this.passwordMismatch) return;

    const newPassword = this.passwordForm.get("newPassword")?.value;

    this.apiError = null;
    this.button_clicked = true;

    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.resetPassword, {
      newPassword,
      passwordResetToken: this.token,
    });

    if (res.errorCode !== 0) {
      this.button_clicked = false;
      this.apiError = this.passwordService.parseError(res);
      return;
    }
    this.login(newPassword);
  }

  private async login(password: string): Promise<void> {
    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.login, {
      loginID: this.email,
      password,
      loginMode: "standard",
      ignoreInterruptions: true,
    });

    if (res.errorCode !== 0) {
      return this.handleLoginError(res);
    }

    await this.proceedAfterLogin();
  }

  private async proceedAfterLogin(): Promise<void> {
    await this.gigyaService.waitForSession();

    const { backTo, ...queryParams } = this.route.snapshot.queryParams;
    if (backTo) {
      this.router.navigate([backTo], { queryParams });
      return;
    }

    await this.organizationPicker.proceed();
  }

  private handleLoginError(res: any): void {
    switch (res.errorCode) {
      case 403101:
      case 403102:
        this.router.navigate(["mfa"], {
          queryParamsHandling: "merge",
          queryParams: { regToken: res.regToken },
        });
        break;
      case 403100:
        this.router.navigate(["set-password"], {
          queryParamsHandling: "merge",
          queryParams: { regToken: res.regToken },
        });
        break;
      default:
        this.button_clicked = false;
        this.apiError = this.passwordService.parseError(res);
    }
  }
}
